import moment from "moment";
import React, { useEffect, useState } from "react";
import { AXIOS_GET } from "../../server/api/crud";
import Spinner from "../../Componenten/Spinner";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { getUserdetails } from "../../Localstorage/localstorage";

const parseCalendlyInfo = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

function CalendlyEvents() {
  const userdetails = getUserdetails();
  const details = parseCalendlyInfo(userdetails?.calendly_info);
  const calendlyUserUri = details?.uri || "";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [lastDays, setLastDays] = useState(null);

  const [params, setParams] = useState({
    organization:
      "https://api.calendly.com/organizations/e5ba7299-d262-4435-acc9-67a68893e28f",
    status: "alles",
    count: "100",
    min_start_time: moment().startOf("month").toISOString(),
    sort: "start_time:asc",
    user: calendlyUserUri,
  });

  const fetchData = async (lastDate = null) => {
    try {
      setLoading(true);
      let queryParams = {};

      if (params.status === "alles") {
        if (!params.organization || !details?.uri) {
          console.warn("Missing Calendly user or organization URI.");
          setLoading(false);
          return;
        }

        const minStartTime = lastDate
          ? moment(lastDate).startOf("month").toISOString()
          : moment().startOf("month").toISOString();

          

        queryParams = new URLSearchParams({
          organization: params.organization,
          count: "100",
          min_start_time: minStartTime,
          sort: "start_time:asc",
          user: details?.uri,
          status: "active",
        }).toString();
      } else {
        queryParams = new URLSearchParams({ ...params }).toString();
      }

      const response = await AXIOS_GET(`calendly/scheduled?${queryParams}`);
      const newData = response?.data?.collection || [];
      setData(newData);
    } catch (error) {
      console.error("Error loading scheduled events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.status) {
      fetchData();
    }
  }, [params.status]);

  const handleGetLastItem = async () => {
    if (lastDays) {
      setLastDays(null);
      await fetchData();
    } else {
      const last = data?.[data.length - 1]?.start_time ?? null;
      setLastDays(last);
      await fetchData(last);
    }
  };

  return (
    <div className="min-h-full bg-wa-app p-4 sm:p-6">
      {loading && <Spinner />}

      <div className="rounded-lg border border-wa-border bg-wa-panel p-4 sm:p-5">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              disabled={!lastDays}
              onClick={handleGetLastItem}
              className="rounded-md border border-wa-border bg-wa-panel2 p-2 text-wa-text transition hover:bg-wa-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <div className="rounded-md border border-wa-border bg-wa-panel2 px-3 py-2 text-sm font-medium text-wa-text">
              {data.length > 0
                ? `${moment(data[0]?.start_time).format("DD/MM/YYYY")} - ${moment(
                    data[data.length - 1]?.start_time
                  ).format("DD/MM/YYYY")}`
                : "Geen afspraken gevonden"}
            </div>
            <button
              disabled={!!lastDays}
              onClick={handleGetLastItem}
              className="rounded-md border border-wa-border bg-wa-panel2 p-2 text-wa-text transition hover:bg-wa-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          <select
            id="status"
            name="status"
            value={params.status}
            onChange={(e) =>
              setParams((prev) => ({ ...prev, status: e.target.value }))
            }
            className="rounded-md border border-wa-border bg-wa-panel2 px-3 py-2 text-sm text-wa-text outline-none ring-wa-accent transition focus-visible:ring-2"
          >
            <option value="alles">Alle afspraken</option>
            <option value="active">Toekomstige afspraken</option>
            <option value="canceled">Geannuleerde afspraken</option>
          </select>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.map((item, index) => (
            <article
              className="rounded-lg border border-wa-border bg-wa-panel2 p-4 shadow-sm"
              key={item?.uri || item?.uuid || `${item?.start_time}-${index}`}
            >
              <div className="mb-3">
                <p className="flex items-center gap-2 text-lg font-semibold text-wa-text">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      item.status === "active" ? "bg-green-500" : "bg-red-500"
                    }`}
                  />
                  {item.name}
                </p>
                <p className="text-sm text-wa-muted">
                  {moment(item.start_time).format("LL")}
                </p>
                <p className="text-sm font-medium text-wa-text">
                  {moment(item.start_time).format("HH:mm")} -{" "}
                  {moment(item.end_time).format("HH:mm")}
                </p>
              </div>

              <div className="space-y-2">
                {(item?.invitees || []).map((invitee, idx) => (
                  <div
                    title={invitee?.questions_and_answers?.[0]?.answer}
                    className="rounded-md border border-wa-border bg-wa-panel px-3 py-2"
                    key={`${invitee?.email || "invitee"}-${idx}`}
                  >
                    <p className="text-xs italic text-wa-muted">Klant</p>
                    <p className="text-sm font-medium text-wa-text">
                      {invitee?.name}
                    </p>
                    <p className="text-xs text-wa-muted">{invitee?.email}</p>
                  </div>
                ))}
              </div>

              <div className="mt-3 space-y-2">
                {(item?.creators || []).map((creator, idx) => (
                  <div
                    className="rounded-md border border-green-600/60 bg-wa-panel px-3 py-2"
                    key={`${creator?.user_email || "creator"}-${idx}`}
                  >
                    <p className="text-xs italic text-green-500">Specialist</p>
                    <p className="text-sm font-medium text-wa-text">
                      {creator?.user_name}
                    </p>
                    <p className="text-xs text-wa-muted">{creator?.user_email}</p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        {!loading && data.length === 0 && (
          <div className="rounded-lg border border-dashed border-wa-border bg-wa-panel2 px-4 py-8 text-center text-sm text-wa-muted">
            Geen afspraken gevonden voor de geselecteerde filters.
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendlyEvents;
